import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Modal from '../../../components/ui/Modal';
import { formatDate, formatTime } from '../../../utils/dateUtils';
import { supabase } from '../../../utils/supabase';
import { regeneratePhotoUrl } from '../../../services/checkInService';

const TimesheetEntry = ({ entry, isEmployer, onApprove, onReject, onRefresh }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkInPhoto, setCheckInPhoto] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [failedImage, setFailedImage] = useState(false);
  const [refreshingUrl, setRefreshingUrl] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Fetch check-in photo when modal opens
  useEffect(() => {
    if (showDetailModal && entry) {
      fetchCheckInPhoto();
    }
  }, [showDetailModal, entry]);

  const fetchCheckInPhoto = async () => {
    setLoadingImage(false);
    setFailedImage(false);
    setCheckInPhoto(null);
    
    try {
      let photoData = null;

      // Method 1: Try fetching via shift_id if available
      if (entry.shift_id) {
        const { data, error } = await supabase
          .from('va_checkins')
          .select('photo_url, photo_storage_path')
          .eq('shift_id', entry.shift_id)
          .order('check_in_time', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && data) {
          photoData = data;
        }
      }

      // Method 2: Fallback - try fetching by contract_id and date range
      if (!photoData && entry.contract_id) {
        const entryDate = new Date(entry.start_time);
        const dayStart = new Date(
          entryDate.getFullYear(),
          entryDate.getMonth(),
          entryDate.getDate()
        );
        const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);

        const { data, error } = await supabase
          .from('va_checkins')
          .select('photo_url, photo_storage_path')
          .eq('contract_id', entry.contract_id)
          .gte('check_in_time', dayStart.toISOString())
          .lt('check_in_time', dayEnd.toISOString())
          .order('check_in_time', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && data) {
          photoData = data;
        }
      }

      if (photoData?.photo_url || photoData?.photo_storage_path) {
        setCheckInPhoto(photoData);
        setLoadingImage(true);
      }
    } catch (err) {
      console.error('[TimesheetEntry] Error fetching photo:', err);
    }
  };

  const handleImageLoad = () => {
    setLoadingImage(false);
  };

  const handleImageError = () => {
    setLoadingImage(false);
    setFailedImage(true);
  };

  const handleRefreshUrl = async () => {
    if (!checkInPhoto?.photo_storage_path) return;

    setRefreshingUrl(true);
    setFailedImage(false);

    try {
      const result = await regeneratePhotoUrl(checkInPhoto.photo_storage_path);
      
      if (result.error) {
        throw result.error;
      }

      setCheckInPhoto(prev => ({
        ...prev,
        photo_url: result.url
      }));
    } catch (err) {
      console.error('[TimesheetEntry] Error refreshing photo URL:', err);
      setFailedImage(true);
    } finally {
      setRefreshingUrl(false);
    }
  };

  const renderCheckInPhoto = () => {
    if (!checkInPhoto) return null;

    if (!checkInPhoto.photo_url && !checkInPhoto.photo_storage_path) {
      return (
        <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <Icon name="Image" size={20} className="text-gray-400" />
        </div>
      );
    }

    if (failedImage || refreshingUrl) {
      return (
        <div className="relative w-16 h-16 bg-gray-100 rounded-lg flex flex-col items-center justify-center gap-1 flex-shrink-0">
          <Icon name="AlertCircle" size={16} className="text-gray-400" />
          {checkInPhoto.photo_storage_path && !refreshingUrl && (
            <button
              onClick={handleRefreshUrl}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              title="Regenerate photo URL"
            >
              Refresh
            </button>
          )}
          {refreshingUrl && (
            <Icon name="Loader" size={12} className="text-gray-400 animate-spin" />
          )}
        </div>
      );
    }

    return (
      <div className="relative w-16 h-16 flex-shrink-0">
        {loadingImage && (
          <div className="absolute inset-0 bg-gray-100 rounded-lg flex items-center justify-center z-10">
            <Icon name="Loader" size={16} className="text-gray-400 animate-spin" />
          </div>
        )}
        <button
          onClick={() => setSelectedImage(checkInPhoto.photo_url)}
          className="group relative w-full h-full block overflow-hidden rounded-lg border-2 border-gray-200 hover:border-primary-400 transition-all"
          title="Click to view full photo"
        >
          <img
            src={checkInPhoto.photo_url}
            alt="Check-in photo"
            className={`w-full h-full object-cover group-hover:opacity-75 transition-opacity ${
              loadingImage ? 'opacity-0' : 'opacity-100'
            }`}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all pointer-events-none">
            <Icon 
              name="ZoomIn" 
              size={14} 
              className="text-white opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </div>
        </button>
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-700', label: 'Draft', icon: 'Edit' },
      pending: { color: 'bg-blue-100 text-blue-700', label: 'Pending', icon: 'Clock' },
      submitted: { color: 'bg-blue-100 text-blue-700', label: 'Pending', icon: 'Clock' },
      approved: { color: 'bg-success-100 text-success-700', label: 'Approved', icon: 'CheckCircle' },
      rejected: { color: 'bg-error-100 text-error-700', label: 'Rejected', icon: 'XCircle' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon name={config.icon} size={12} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    
    setIsSubmitting(true);
    try {
      await onReject(entry.id, rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
    } catch (error) {
      console.error('Error rejecting entry:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCardClick = (e) => {
    // Don't open modal if clicking on buttons or their children
    if (
      e.target.closest('button') || 
      e.target.tagName === 'BUTTON'
    ) {
      return;
    }
    setShowDetailModal(true);
  };

  const canManageEntry = isEmployer && (entry.status === 'pending' || entry.status === 'submitted');

  return (
    <>
      <div 
        className="p-6 hover:bg-secondary-50 transition-colors duration-200 cursor-pointer"
        onClick={handleCardClick}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-sm font-medium text-text-primary">
                {formatDate(entry.date)}
              </h3>
              {getStatusBadge(entry.status)}
              <span className="text-xs text-text-secondary flex items-center">
                <Icon name="Eye" size={14} className="mr-1" />
                Click to view timeline
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
              <div>
                <p className="text-xs text-text-secondary">Time</p>
                <p className="text-sm text-text-primary">
                  {formatTime(entry.start_time)} - {entry.end_time ? formatTime(entry.end_time) : 'In Progress'}
                </p>
              </div>
              
              <div>
                <p className="text-xs text-text-secondary">Hours</p>
                <p className="text-sm font-medium text-text-primary">
                  {entry.hours_worked || 0}h
                </p>
              </div>
              
              <div>
                <p className="text-xs text-text-secondary">Rate</p>
                <p className="text-sm text-text-primary">
                  ${entry.hourly_rate || 0}/hr
                </p>
              </div>
              
              <div>
                <p className="text-xs text-text-secondary">Amount</p>
                <p className="text-sm font-medium text-text-primary">
                  ${entry.total_amount || 0}
                </p>
              </div>
            </div>

            {entry.description && (
              <div className="mb-3">
                <p className="text-xs text-text-secondary mb-1">Description</p>
                <p className="text-sm text-text-primary">{entry.description}</p>
              </div>
            )}

            {entry.task_completed && (
              <div className="mb-3">
                <p className="text-xs text-text-secondary mb-1">Task Completed</p>
                <p className="text-sm text-text-primary">{entry.task_completed}</p>
              </div>
            )}

            {entry.status === 'rejected' && entry.rejection_reason && (
              <div className="mt-3 p-3 bg-error-50 border border-error-200 rounded-lg">
                <p className="text-xs text-error-600 mb-1">Rejection Reason</p>
                <p className="text-sm text-error-700">{entry.rejection_reason}</p>
              </div>
            )}

            {entry.status === 'approved' && entry.approved_at && (
              <div className="mt-3 text-xs text-success-600">
                Approved on {formatDate(entry.approved_at)}
              </div>
            )}
          </div>

          {canManageEntry && (
            <div className="flex items-center space-x-2 ml-4">
              <Button
                variant="success"
                size="sm"
                iconName="Check"
                onClick={() => onApprove(entry.id)}
              >
                Approve
              </Button>
              <Button
                variant="error"
                size="sm"
                iconName="X"
                onClick={() => setShowRejectModal(true)}
              >
                Reject
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Timeline Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title={`Timeline - ${formatDate(entry.date)}`}
        size="xl"
      >
        <div className="max-h-[70vh] overflow-y-auto">
          {/* Entry Details in Timeline Format */}
          <div className="space-y-4">
            {/* Summary Header */}
            <div className="pb-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">
                    {formatTime(entry.start_time)} - {entry.end_time ? formatTime(entry.end_time) : 'In Progress'}
                  </p>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {entry.hours_worked || 0} hours • ${entry.total_amount || 0}
                  </p>
                </div>
                {getStatusBadge(entry.status)}
              </div>
            </div>

            {/* Entry Card */}
            <div className={`border-l-4 rounded-lg p-4 shadow-sm ${
              entry.status === 'approved' ? 'border-green-500 bg-green-50' :
              entry.status === 'pending' ? 'border-yellow-500 bg-yellow-50' :
              entry.status === 'rejected' ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-gray-50'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 mr-4">
                  {/* Time Information */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Clock" size={16} className="text-gray-600" />
                      <span className="font-medium text-gray-700">
                        {formatTime(entry.start_time)}
                        {entry.end_time && ` - ${formatTime(entry.end_time)}`}
                      </span>
                      <span className="text-gray-500">
                        ({(entry.hours_worked || 0).toFixed(2)} {entry.hours_worked === 1 ? 'hour' : 'hours'})
                      </span>
                    </div>
                  </div>

              {/* Description */}
              {entry.description && (
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name="FileText" size={14} className="text-gray-600" />
                    <span className="text-xs text-gray-600 font-medium">Description</span>
                  </div>
                  <div className="text-sm text-gray-700 bg-white rounded p-3 border border-gray-200">
                    {entry.description}
                  </div>
                </div>
              )}

              {/* Task Completed */}
              {entry.task_completed && (
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name="CheckSquare" size={14} className="text-gray-600" />
                    <span className="text-xs text-gray-600 font-medium">Task Completed</span>
                  </div>
                  <div className="text-sm text-gray-700 bg-white rounded p-3 border border-gray-200">
                    {entry.task_completed}
                  </div>
                </div>
              )}
                </div>

                {/* Check-in Photo Thumbnail */}
                <div className="flex-shrink-0 ml-auto">
                  {renderCheckInPhoto()}
                </div>
              </div>

              {/* Rate and Amount */}
              <div className="grid grid-cols-3 gap-4 p-3 bg-white rounded-lg border border-gray-200">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Hours</p>
                  <p className="text-sm font-semibold text-gray-900">{entry.hours_worked || 0}h</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Rate</p>
                  <p className="text-sm font-semibold text-gray-900">${entry.hourly_rate || 0}/hr</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Amount</p>
                  <p className="text-sm font-semibold text-gray-900">${entry.total_amount || 0}</p>
                </div>
              </div>

              {/* Rejection Reason */}
              {entry.status === 'rejected' && entry.rejection_reason && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-600 mb-1 font-medium">Rejection Reason</p>
                  <p className="text-sm text-red-700">{entry.rejection_reason}</p>
                </div>
              )}

              {/* Approval Information */}
              {entry.status === 'approved' && entry.approved_at && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-xs text-green-600 mb-1 font-medium">Approved</p>
                  <p className="text-sm text-green-700">
                    {formatDate(entry.approved_at)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Photo Lightbox Modal */}
      {selectedImage && (
        <div onMouseDown={(e) => e.stopPropagation()}>
          <Modal
            isOpen={!!selectedImage}
            onClose={() => setSelectedImage(null)}
            title="Check-in Photo"
            size="lg"
          >
            <div className="flex items-center justify-center p-4" onMouseDown={(e) => e.stopPropagation()}>
              <img
                src={selectedImage}
                alt="Full size check-in"
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          </Modal>
        </div>
      )}

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject Time Entry"
      >
        <div className="space-y-4">
          <p className="text-text-secondary">
            Please provide a reason for rejecting this time entry:
          </p>
          
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            rows={4}
            placeholder="Enter rejection reason..."
            required
          />
          
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowRejectModal(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="error"
              onClick={handleReject}
              loading={isSubmitting}
              disabled={!rejectReason.trim()}
            >
              Reject Entry
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TimesheetEntry;
